import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import SuperHeader, { apiErr } from "@/components/superadmin/SuperHeader";
import StatusAction, { STATUS_BADGE } from "@/components/superadmin/StatusAction";
import OverviewTab from "@/components/superadmin/tabs/OverviewTab";
import UsersTab from "@/components/superadmin/tabs/UsersTab";
import WhatsAppTab from "@/components/superadmin/tabs/WhatsAppTab";
import ContactsTab from "@/components/superadmin/tabs/ContactsTab";
import LeadsTab from "@/components/superadmin/tabs/LeadsTab";
import ConversationsTab from "@/components/superadmin/tabs/ConversationsTab";
import CampaignsTab from "@/components/superadmin/tabs/CampaignsTab";
import UsageTab from "@/components/superadmin/tabs/UsageTab";
import SettingsTab from "@/components/superadmin/tabs/SettingsTab";
import ActivityTab from "@/components/superadmin/tabs/ActivityTab";

const TABS = [
  ["overview", "Overview", OverviewTab], ["users", "Users", UsersTab], ["whatsapp", "WhatsApp", WhatsAppTab],
  ["contacts", "Contacts", ContactsTab], ["leads", "Leads", LeadsTab], ["conversations", "Conversations", ConversationsTab],
  ["campaigns", "Campaigns", CampaignsTab], ["usage", "Usage & Analytics", UsageTab], ["settings", "Settings", SettingsTab],
  ["activity", "Activity Log", ActivityTab],
];

const tabsFor = (d) => (d.company.status === "deleted" ? TABS.filter(([k]) => k === "overview" || k === "activity") : TABS);

export default function SuperCompanyDetail() {
  const { cid } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const back = loc.state?.from || sessionStorage.getItem("sa_list") || "/super-admin";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(new URLSearchParams(loc.search).get("tab") || "overview");

  useEffect(() => { if (loc.state?.from) sessionStorage.setItem("sa_list", loc.state.from); }, [loc.state]);
  const load = () => { setErr(null); api.get(`/admin/companies/${cid}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.status === 404 ? "Company not found" : apiErr(e))); };
  useEffect(load, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeTab = (t) => { setTab(t); nav({ search: `?tab=${t}` }, { replace: true, state: loc.state }); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-detail-page">
      <SuperHeader />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4">
        <Breadcrumb data-testid="company-breadcrumbs">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/super-admin">Super Admin</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to={back} data-testid="breadcrumb-companies">Companies</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{d?.company?.name || "…"}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Button variant="outline" size="sm" onClick={() => nav(back)} data-testid="back-to-companies"><ArrowLeft className="w-4 h-4 mr-1" />Back to Companies</Button>

        {err ? (
          <Card className="p-10 text-center text-rose-700" data-testid="company-error"><AlertCircle className="w-6 h-6 mx-auto mb-2" />{err}</Card>
        ) : !d ? (
          <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-64" /></div>
        ) : (
          <>
            <Card className="p-5 border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900" data-testid="company-name">{d.company.name}</h1>
                  <Badge className={`${STATUS_BADGE[d.company.status] || ""} capitalize`} data-testid="company-status">{d.company.status}</Badge>
                  {d.company.is_test && <Badge className="bg-amber-100 text-amber-800">TEST / DEMO</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono" data-testid="company-id">ID: {d.company.id}</div>
              </div>
              <StatusAction company={d.company} onDone={load} />
            </Card>
            <Tabs value={tab} onValueChange={changeTab}>
              <TabsList className="flex flex-wrap h-auto justify-start bg-white border border-slate-200">
                {tabsFor(d).map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{l}</TabsTrigger>)}
              </TabsList>
              {tabsFor(d).map(([k, , C]) => (
                <TabsContent key={k} value={k} className="mt-4">{tab === k && <C cid={cid} detail={d} reload={load} />}</TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
